/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { tokens } from "../../../../theme";
import Header from "../../../../components/Header";
import { Box, Typography, useTheme } from "@mui/material";
import DataGridButton from "../../../../components/DataGridButton";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { dataGridButtonTypography } from "../../../../styles/DataGridStyles";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { DbSoftwareBuildsStyles } from "../../styles";
import { dataGridButtonStyles } from "../../../../styles/buttonStyles";
import {
  useDeleteDbSoftwareBuild,
  useListAllDbSoftwareBuilds,
  useRetryDbSoftwareBuild,
} from "../../../../api/softwareImages";
import moment from "moment";
import { CloudIcon, EngineIcon } from "../../Utils";
import DetailPanelContent from "./DetailedPanelContent";
import { useListResources } from "../../../../api/iam";
import { timeFormat } from "../../../../utils/constants";
import { useNotification } from "../../../../components/Notification";
import { useConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { permissionListIncludes } from "../../../../utils/helperFunctions";
import { useReportModal } from "../../../../components/ReportModal";
import { tabbedDataGridPage } from "../../../../styles/commonPageStyles";
import { useReuploadDbPatchScriptDialog } from "../../Dialogs/ReuploadDbPatchScriptDialog";

const buildsAdapter = (builds) => {
  return builds.map((build) => {
    return {
      ...build,
      engine: build?.inputData?.engine,
      releaseBranch: build?.inputData?.releaseBranch,
      patchVersionName: build?.inputData?.patchVersionName,
      tenantName: build?.inputData?.tenantUrl?.split(".")?.[0],
    };
  });
};

const DbSoftwareBuilds = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [builds, setBuilds] = useState([]);
  const [reload, setReload] = useState(false);

  const { notificationComponent, displayNotification } = useNotification();
  const { reportModal, setReportModalOpen } = useReportModal();

  const {
    postData: getBuilds,
    response: getBuildsResp,
    isLoading: isLoadingBuilds,
  } = useListAllDbSoftwareBuilds();

  const {
    confirmationDialog: deleteDialog,
    showConfirmationDialog: showDeleteDialog,
  } = useConfirmationDialog(
    "Delete Build",
    useDeleteDbSoftwareBuild,
    displayNotification,
    getBuilds
  );

  const {
    confirmationDialog: retryDialog,
    showConfirmationDialog: showRetryDialog,
  } = useConfirmationDialog(
    "Retry Build",
    useRetryDbSoftwareBuild,
    displayNotification,
    getBuilds
  );

  const { reuploadDbPatchScriptDialog, setOpenReuploadDbPatchScriptDialog } =
    useReuploadDbPatchScriptDialog(() => getBuilds(), displayNotification);

  const deletePermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "DELETE",
    "DELETE",
    [reload]
  );

  const retryPermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "POST",
    "RETRY",
    [reload]
  );

  const reuploadPermissionList = useListResources(
    "/apps/tessell/software-images/db-software/builds",
    "PATCH",
    "REUPLOAD_SCRIPTS",
    [reload]
  );

  useEffect(() => {
    getBuilds();
  }, [reload]);

  useEffect(() => {
    setBuilds(buildsAdapter(getBuildsResp?.dbSoftwareBuilds || []));
  }, [getBuildsResp]);

  const columns = [
    {
      field: "patchVersionName",
      flex: 2.5,
      cellClassName: "name-column--cell",
      renderHeader: () => <Typography variant="h5">Name</Typography>,
      renderCell: ({ row: { patchVersionName } }) => (
        <Typography variant="h5">{patchVersionName || ""}</Typography>
      ),
    },
    {
      field: "jiraId",
      flex: 1.5,
      renderHeader: () => <Typography variant="h5">JiraId</Typography>,
      renderCell: ({ row: { jiraId } }) => (
        <Typography variant="h5">{jiraId || ""}</Typography>
      ),
    },
    {
      field: "engine",
      flex: 1,
      renderHeader: () => <Typography variant="h5">Engine</Typography>,
      renderCell: ({ row: { engine } }) => <EngineIcon engine={engine} />,
    },
    {
      field: "cloud",
      flex: 1,
      renderHeader: () => <Typography variant="h5">cloud</Typography>,
      renderCell: ({ row: { cloud } }) => (
        <CloudIcon cloud={cloud?.toLowerCase()} />
      ),
    },
    {
      field: "releaseBranch",
      renderHeader: () => <Typography variant="h5">Branch</Typography>,
      flex: 1,
      renderCell: ({ row: { releaseBranch } }) => (
        <Typography variant="h5">{releaseBranch || ""}</Typography>
      ),
    },
    {
      field: "tenantName",
      renderHeader: () => <Typography variant="h5">Tenant</Typography>,
      flex: 2,
      renderCell: ({ row: { tenantName } }) => (
        <Typography variant="h5">{tenantName || ""}</Typography>
      ),
    },
    {
      field: "triggeredBy",
      renderHeader: () => <Typography variant="h5">Triggered By</Typography>,
      flex: 3,
      renderCell: ({ row: { triggeredBy } }) => (
        <Typography variant="h5">{triggeredBy || ""}</Typography>
      ),
    },
    {
      field: "dateCreated",
      renderHeader: () => <Typography variant="h5">Date Created</Typography>,
      flex: 3,
      renderCell: ({ row: { dateCreated } }) => (
        <Typography variant="h5">
          {moment(dateCreated).format(timeFormat) || ""}
        </Typography>
      ),
    },
    {
      field: "dateModified",
      renderHeader: () => <Typography variant="h5">Date Modified</Typography>,
      flex: 3,
      renderCell: ({ row: { dateModified } }) => (
        <Typography variant="h5">
          {moment(dateModified).format(timeFormat) || ""}
        </Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.5,
      getActions: ({ row }) =>
        [
          permissionListIncludes(deletePermissionList, row?.id) && (
            <GridActionsCellItem
              label="Delete"
              onClick={() =>
                showDeleteDialog("Permanently delete this build data?", row?.id)
              }
              showInMenu
            />
          ),
          permissionListIncludes(reuploadPermissionList, row?.id) && (
            <GridActionsCellItem
              label="Reupload Scripts"
              onClick={() =>
                setOpenReuploadDbPatchScriptDialog(row?.id, row?.runData?.steps)
              }
              showInMenu
            />
          ),
        ].filter(Boolean),
    },
  ];

  return (
    <Box sx={tabbedDataGridPage.tabContentMainBox}>
      {notificationComponent}
      {deleteDialog}
      {retryDialog}
      {reuploadDbPatchScriptDialog}
      {reportModal}
      <Grid sx={tabbedDataGridPage.gridContainer} container columns={2}>
        <Grid xs={1}>
          <Header
            title="DB Software Builds"
            subtitle="View and manage db software builds"
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => setReload((prev) => !prev)}
            >
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>

      <Box sx={DbSoftwareBuildsStyles.dataGridBoxStyles(colors)}>
        <DataGridPro
          disableSelectionOnClick
          loading={isLoadingBuilds}
          rows={builds}
          columns={columns}
          getDetailPanelContent={({ row }) => (
            <DetailPanelContent
              row={row}
              showRetryDialog={showRetryDialog}
              retryPermissionList={retryPermissionList}
              setReportModalOpen={setReportModalOpen}
            />
          )}
          getDetailPanelHeight={() => "auto"}
        />
      </Box>
    </Box>
  );
};

export default DbSoftwareBuilds;
